import React from 'react';
import { useContext, useEffect, useCallback } from "react";
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { Language } from "../languages"
import { LanguageContext, Complexity, RateCardOverride } from "../Helpers/Context";
import useAxios from 'axios-hooks';

export default function RateCardDetailOverride() {
    const { language, dictionary, rateCard, rateCardOverride, setRateCardOverride, officeId } = useContext(LanguageContext);
    const [hasChange, setHasChange] = React.useState(false);
    const [isDefault, setIsDefault] = React.useState(true);
    const [newOverride, setNewOverride] = React.useState<RateCardOverride>({
        officeId: 0,
        rateId: 0,
        complexityId: 0,
        rate: 0,
        rowStatus: "A"
    });

    const [{ data, loading }] = useAxios<Complexity[]>(
        "Complexity?taxEngineId=1"
    );

    const handleComplexityChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setHasChange(true);
        setNewOverride({ ...newOverride, complexityId: event.target.value as number });
    };

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        let newValue = parseFloat(event.target.value as string);
        setHasChange(newValue > 0 || hasChange);
        let truncValue = Math.round(newValue * 100) / 100;
        setNewOverride({ ...newOverride, rate: truncValue });
    };

    const cancelChange = () => {
        setInitialValues();
    };

    async function useDefault() {
        if (rateCard && rateCardOverride) {
            await executePostDelete();
            setRateCardOverride({ ...rateCardOverride, rowStatus: "D" });
            setHasChange(false);
        }
    };

    async function saveChange() {
        if (rateCard) {
            await executePostActive();
            setRateCardOverride(newOverride);
            setHasChange(false);
        }
    };

    const setInitialValues = useCallback(() => {
        let initOverride: RateCardOverride =
        {
            officeId: officeId,
            rateId: rateCard?.rateID ?? 0,
            complexityId: 0,
            rate: 0,
            rowStatus: "A"
        }

        if (rateCardOverride && rateCardOverride.rowStatus === "A") {
            initOverride.rate = rateCardOverride.rate;
            initOverride.complexityId = rateCardOverride.complexityId;
        }
        else {
            initOverride.rate = rateCard?.rate ?? 0;
            initOverride.complexityId = rateCard?.complexityID ?? 0;
        }
        setNewOverride(initOverride);
        setHasChange(false);
        setIsDefault(rateCardOverride?.rowStatus !== "A");
    }, [rateCard, rateCardOverride, officeId]);

    const [, executePostActive] = useAxios(
        {
            url: `RateCard/Override?officeId=${newOverride.officeId}&rateId=${newOverride.rateId}&complexityId=${newOverride.complexityId}&rate=${newOverride.rate}&status=A`,
            method: "POST"
        },
        { manual: true }
    );

    const [, executePostDelete] = useAxios(
        {
            url: `RateCard/Override?officeId=${rateCardOverride?.officeId}&rateId=${rateCardOverride?.rateId}&complexityId=${rateCardOverride?.complexityId}&rate=${rateCardOverride?.rate}&status=D`,
            method: "POST"
        },
        { manual: true }
    );

    let cardComplexity = data?.find((x) => x.complexityId === rateCard?.complexityID);

    useEffect(() => {
        setInitialValues();
    }, [rateCard, rateCardOverride, setInitialValues]);

    return (
        <React.Fragment>
            <Typography variant="h6" color="primary">
                {dictionary.rateCardDetailTitle}
            </Typography>
            {loading && (
                <CircularProgress />
            )}
            {data && !loading && rateCard && (
                <FormControl>
                    <Grid container spacing={1} alignItems="flex-start" direction="column">
                        <Grid item xs={12} >
                            <Typography variant="subtitle1" display="inline" color="primary">
                                {dictionary.rateCardDetailRateCard}:
                            </Typography>
                            {language === Language.English ? rateCard?.descrip : rateCard?.descripFr}
                        </Grid>
                        <Grid item xs={12} >
                            <Typography variant="subtitle1" display="inline" color="primary">
                                {dictionary.rateCardDetailDefaultComplexity}:
                            </Typography>
                            {language === Language.English ? cardComplexity?.description : cardComplexity?.descriptionFr}
                        </Grid>
                        <Grid item xs={12} >
                            <Typography variant="subtitle1" display="inline" color="primary">
                                {dictionary.rateCardDetailDefaultAmount}:
                            </Typography>
                            ${rateCard?.rate.toFixed(2)}
                        </Grid>
                        <Grid item xs={12} >
                            <Typography variant="subtitle1" display="inline" color="primary">
                                {dictionary.rateCardDetailCurrentComplexity}:
                            </Typography>
                            <TextField select onChange={handleComplexityChange}
                                value={newOverride.complexityId === 0 ? '' : newOverride.complexityId} >
                                {data?.map((card) =>
                                (
                                    <MenuItem
                                        key={card.complexityId}
                                        value={card.complexityId}
                                    >
                                        {language === Language.English ? card.description : card.descriptionFr}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Typography variant="body2" display="inline">
                                ({isDefault ? dictionary.complexityDefault : dictionary.complexityCustom})
                            </Typography>
                        </Grid>
                        <Grid item xs={12} >
                            <Typography variant="subtitle1" display="inline" color="primary">
                                {dictionary.rateCardDetailCurrentAmount}:
                            </Typography>
                            <TextField
                                type='number'
                                InputProps={{
                                    readOnly: rateCard?.isInfoItem === "Y",
                                }}
                                onChange={handleChange}
                                value={newOverride.rate} />
                            <Typography variant="body2" display="inline">
                                ({isDefault ? dictionary.complexityDefault : dictionary.complexityCustom})
                            </Typography>
                        </Grid>
                        <Grid item xs={12} ></Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid item xs="auto" >
                            <Button variant="contained" color="primary" disabled={!hasChange} onClick={saveChange}>{dictionary.save}</Button>
                        </Grid>
                        <Grid item xs="auto" >
                            <Button variant="contained" color="primary" disabled={!hasChange} onClick={cancelChange}>{dictionary.cancel}</Button>
                        </Grid>
                        <Grid item xs ></Grid>
                        <Grid item xs="auto" >
                            <Button variant="contained" color="primary" disabled={isDefault} onClick={useDefault}>{dictionary.useDefault}</Button>
                        </Grid>
                    </Grid>
                </FormControl>
            )}
        </React.Fragment>
    );
}